if (!customElements.get('featured-prints')) {
  class FeaturedPrints extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.fp-track');
      this.progressFill = this.querySelector('.js-fp-progress');
      this.featuredImgs = this.querySelectorAll('.fp-featured-img');

      // Nav buttons
      this.btnPrev = this.querySelector('.js-fp-prev');
      this.btnNext = this.querySelector('.js-fp-next');
      this.btnBotPrev = this.querySelector('.js-fp-bottom-prev');
      this.btnBotNext = this.querySelector('.js-fp-bottom-next');

      this.activeIndex = 0;
      this._rafId = null;

      this.init();
    }

    /* ─────────────────────────────────────────
       INIT
    ───────────────────────────────────────── */
    init() {
      if (!this.track) return;

      this.cards = Array.from(this.track.querySelectorAll('.fp-card'));
      if (this.cards.length === 0) return;

      // Wire nav buttons
      [this.btnPrev, this.btnBotPrev].filter(Boolean).forEach(btn =>
        btn.addEventListener('click', () => this._navigate('prev'))
      );
      [this.btnNext, this.btnBotNext].filter(Boolean).forEach(btn =>
        btn.addEventListener('click', () => this._navigate('next'))
      );

      // Hover → sync lifestyle image on desktop
      this.cards.forEach((card, i) =>
        card.addEventListener('mouseenter', () => this._setActive(i))
      );

      // Add-to-cart feedback (AJAX drawer)
      this.querySelectorAll('.fp-card form').forEach(form => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          const btn = form.querySelector('.fp-add-btn');
          if (!btn || btn.disabled) return;
          const orig = btn.textContent;
          btn.textContent = 'Adding…';
          btn.disabled = true;

          try {
            const formData = new FormData(form);
            const variantId = parseInt(formData.get('id'));
            if (!variantId) throw new Error('Missing variant id');

            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                id: variantId,
                quantity: 1,
                sections: ['cart-drawer', 'cart-icon-bubble'],
                sections_url: window.location.pathname
              })
            });

            if (!res.ok) throw new Error('Could not add to cart');
            const parsedState = await res.json();

            // Update cart bubble
            const bubbleHtml = parsedState.sections?.['cart-icon-bubble'];
            if (bubbleHtml) {
              const tempBubble = document.createElement('div');
              tempBubble.innerHTML = bubbleHtml;
              const newBubble = tempBubble.querySelector('#cart-icon-bubble');
              const currentBubble = document.getElementById('cart-icon-bubble');
              if (newBubble && currentBubble) currentBubble.innerHTML = newBubble.innerHTML;
            }

            // Open cart drawer
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              if (typeof cartDrawer.renderContents === 'function') {
                cartDrawer.renderContents(parsedState);
              } else {
                const drawerHtml = parsedState.sections?.['cart-drawer'];
                if (drawerHtml) {
                  const tempDrawer = document.createElement('div');
                  tempDrawer.innerHTML = drawerHtml;
                  const newDrawer = tempDrawer.querySelector('cart-drawer') || tempDrawer.firstElementChild;
                  if (newDrawer) cartDrawer.innerHTML = newDrawer.innerHTML;
                }
                cartDrawer.classList.add('active', 'animate', 'is-open');
                cartDrawer.classList.remove('is-empty');
                const innerDrawer = cartDrawer.querySelector('.drawer');
                if (innerDrawer) innerDrawer.classList.add('active', 'animate', 'is-open');
                document.body.classList.add('overflow-hidden');
              }
            } else {
              const cartNotification = document.querySelector('cart-notification');
              if (cartNotification && typeof cartNotification.renderContents === 'function') {
                cartNotification.renderContents(parsedState);
              } else if (cartNotification && typeof cartNotification.open === 'function') {
                cartNotification.open();
              } else {
                const cartIcon = document.getElementById('cart-icon-bubble');
                if (cartIcon) cartIcon.click();
                else window.location.href = window.routes?.cart_url || '/cart';
              }
            }

            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: parsedState }));

            btn.textContent = 'Added ✓';
            btn.classList.add('is-added');
            setTimeout(() => { btn.textContent = orig; btn.disabled = false; btn.classList.remove('is-added'); }, 2000);
          } catch (err) {
            console.error('Add to cart error:', err);
            btn.textContent = 'Error';
            setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 2000);
          }
        });
      });

      // rAF-throttled scroll → update active + progress + buttons
      this.track.addEventListener('scroll', () => {
        if (this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          this._onScroll();
        });
      }, { passive: true });

      // Resize → re-anchor + update
      window.addEventListener('resize', () => {
        this._snapTo(this.activeIndex, false);
        this._updateProgress();
        this._updateButtons();
      });

      // Boot
      this._snapTo(0, false);
      this._setActive(0);
      this._updateProgress();
      this._updateButtons();

      this.initDragToScroll();
    }

    initDragToScroll() {
      this.isDown = false;
      this.startX = 0;
      this.scrollLeft = 0;
      this.isDragging = false;

      this.track.addEventListener('mousedown', (e) => {
        this.isDown = true;
        this.isDragging = false;
        this.startX = e.pageX - this.track.offsetLeft;
        this.scrollLeft = this.track.scrollLeft;
      });

      this.track.addEventListener('mouseleave', () => {
        if (!this.isDown) return;
        this.isDown = false;
        this.track.classList.remove('is-dragging');
        this._snapTo(this.activeIndex, true);
      });

      this.track.addEventListener('mouseup', () => {
        if (!this.isDown) return;
        this.isDown = false;
        setTimeout(() => {
          this.track.classList.remove('is-dragging');
          this._snapTo(this.activeIndex, true);
        }, 10);
      });

      this.track.addEventListener('mousemove', (e) => {
        if (!this.isDown) return;

        const x = e.pageX - this.track.offsetLeft;
        const walk = (x - this.startX) * 1.5; // Scroll speed multiplier

        if (Math.abs(walk) > 5) {
          this.isDragging = true;
          this.track.classList.add('is-dragging');
          e.preventDefault();
        }

        if (this.isDragging) {
          this.track.scrollLeft = this.scrollLeft - walk;
        }
      });

      this.track.addEventListener('click', (e) => {
        if (this.isDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }

    /* ─────────────────────────────────────────
       NAVIGATE (button click)
    ───────────────────────────────────────── */
    _navigate(dir) {
      const n = this.cards.length;
      const next = this.activeIndex + (dir === 'next' ? 1 : -1);
      // Clamp to first / last – no wrapping
      const clamped = Math.max(0, Math.min(n - 1, next));
      this._snapTo(clamped, true);
      this._setActive(clamped);
    }

    /* ─────────────────────────────────────────
       SCROLL HANDLER (user drag / swipe)
    ───────────────────────────────────────── */
    _onScroll() {
      this._syncActiveFromScroll();
      this._updateProgress();
      this._updateButtons();
    }

    /* Find whichever card is closest to the left edge of the viewport */
    _syncActiveFromScroll() {
      const sl = this.track.scrollLeft;
      let minD = Infinity, idx = 0;
      this.cards.forEach((card, i) => {
        const d = Math.abs((card.offsetLeft - this.track.offsetLeft) - sl);
        if (d < minD) { minD = d; idx = i; }
      });
      if (idx !== this.activeIndex) {
        this.activeIndex = idx;
        this._syncUI();
      }
    }

    /* ─────────────────────────────────────────
       ACTIVE STATE
    ───────────────────────────────────────── */
    _setActive(idx) {
      this.activeIndex = idx;
      this._syncUI();
      this._updateButtons();
    }

    _syncUI() {
      const idx = this.activeIndex;
      // Left panel lifestyle images
      this.featuredImgs.forEach((img, i) => img.classList.toggle('is-active', i === idx));
      // Active border on cards
      this.cards.forEach((card, i) => card.classList.toggle('is-active-card', i === idx));
    }

    /* ─────────────────────────────────────────
       SNAP – scroll to a specific card index
    ───────────────────────────────────────── */
    _snapTo(idx, smooth) {
      const card = this.cards[idx];
      if (!card) return;
      const left = card.offsetLeft - this.track.offsetLeft;
      this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
      this._updateProgress();
    }

    /* ─────────────────────────────────────────
       PROGRESS BAR  (0 % → 100 % across all cards)
    ───────────────────────────────────────── */
    _updateProgress() {
      if (!this.progressFill || this.cards.length === 0) return;
      const first = this.cards[0];
      const last = this.cards[this.cards.length - 1];
      const start = first.offsetLeft - this.track.offsetLeft;
      const end = (last.offsetLeft - this.track.offsetLeft)
        + last.offsetWidth
        - this.track.clientWidth;
      const range = end - start;
      const raw = range > 0 ? (this.track.scrollLeft - start) / range : 0;
      const pct = Math.min(100, Math.max(0, raw * 100));
      this.progressFill.style.width = pct + '%';
    }

    /* ─────────────────────────────────────────
       BUTTON STATE  (disable at edges)
    ───────────────────────────────────────── */
    _updateButtons() {
      const atStart = this.activeIndex === 0;
      const atEnd = this.activeIndex === this.cards.length - 1;

      [this.btnPrev, this.btnBotPrev].filter(Boolean).forEach(btn => {
        btn.disabled = atStart;
      });
      [this.btnNext, this.btnBotNext].filter(Boolean).forEach(btn => {
        btn.disabled = atEnd;
      });
    }
  }

  customElements.define('featured-prints', FeaturedPrints);
}
